import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';;
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, mergeMap, startWith } from 'rxjs/operators';
import { Company, Vacancy, VacancyCandidate } from '../../../core/models';
import { UserService } from '../../../core/services';
import { CleanObjectService } from '../../../core/services';
import { VacanciesService } from '../../vacancies.service';
import { VacancyCandidatesService } from '../../vacancy-candidates.service';
import { ToastrService } from 'ngx-toastr';
import { BulkActionsLoadingModalComponent } from '../bulk-actions-loading-modal/bulk-actions-loading-modal.component';
import { faBriefcaseBlank, faGraduationCap } from '@fortawesome/pro-regular-svg-icons';

@Component({
  selector: 'app-move-candidate-to-another-opportunity-modal',
  templateUrl: './move-candidate-to-another-opportunity-modal.component.html',
  styleUrls: ['./move-candidate-to-another-opportunity-modal.component.scss']
})
export class MoveCandidateToAnotherOpportunityModalComponent implements OnInit, OnDestroy {
  public searchVacancyName$ = new Subject<string>();
  @Input() rootType: string = 'vacancies' as string;
  @Input() candidates: VacancyCandidate[];
  @Input() vacancy: Vacancy;
  @Input() company: Company;
  @Input() bulkCandidatesIds: string[] = [];
  @Input() limitCandidatesPerRequest: number = 1;

  public STATE: 'IDLE' | 'RUNNING' | 'FINALIZED' = 'IDLE';
  public loading: 'IDLE' | 'RUNNING' | 'FINALIZED' = 'IDLE';
  public selectedVacancies: any[] = [];
  public selectedVacancy: any;
  public availableVacancies: Vacancy[] = [];
  public icons: any = {
    vacancy: faBriefcaseBlank,
    course: faGraduationCap
  };

  private availableVacancies$: Subscription;
  private LENGTH: number;
  private vacancyCandidateIds: string[];

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private userService: UserService,
    private vacanciesService: VacanciesService,
    private vacancyCandidatesService: VacancyCandidatesService,
    private cleanObjectService: CleanObjectService,
    private toastrService: ToastrService
  ) { }

  ngOnInit() {
    this.LENGTH = this.limitCandidatesPerRequest ? this.limitCandidatesPerRequest : 50;

    this.vacancyCandidateIds = this.generateCandidateIdsInBulk(this.candidates.map((candidate: any) => candidate._id));

    this.searchVacancyName$
      .pipe(startWith(''), debounceTime(300), distinctUntilChanged())
      .subscribe((officeName: string) => {
        this.STATE = 'RUNNING';
        const params: any = this.cleanObjectService.getCleanedAttribute({
          filters: {
            office: officeName,
            status: 'published',
            vacancyId: this.vacancy._id,
            isEducationCourse: typeof this.vacancy.isEducationCourse === 'boolean' ? this.vacancy.isEducationCourse : false
          }
        });

        this.availableVacancies$ = this.vacanciesService.getVacanciesAvailableForTransfer(params)
          .subscribe(res => {
            this.availableVacancies = res.data;
            this.STATE = 'FINALIZED';
          })
      })
  }

  ngOnDestroy(): void {
    this.availableVacancies$.unsubscribe();
    this.searchVacancyName$.unsubscribe();
  }

  private generateCandidateIdsInBulk(candidateIdOnScreen: string[]): string[] {
    if (!this.bulkCandidatesIds || !this.bulkCandidatesIds.length) {
      return candidateIdOnScreen; 
    }

    return Array.from(new Set(candidateIdOnScreen.concat(this.bulkCandidatesIds)));
  }

  public getUserFullName(user: any): string {
    return this.userService.getUserFullName(user);
  }

  public sendVacancyCandidateForAnotherOpportunity(): void {
    if (this.loading === 'RUNNING') {
      return;
    }

    if (!this.selectedVacancies.length) {
      this.toastrService.clear();
      this.toastrService.info('Selecione ao menos uma oportunidade.', 'Oportunidade não selecionada!');
      return;
    }

    this.repeatProcess();
  }

  private async repeatProcess(): Promise<void> {
    const requests: Observable<any>[] = [];
    this.loading = 'RUNNING';
    let index = 0, modal, interval;
    const numberOfSimultaneousRequests: number = 10;

    do {
      const vacancyCandidateIds = Object.assign([], this.vacancyCandidateIds.splice(0, this.vacancyCandidateIds.length <
        this.LENGTH ?
        this.vacancyCandidateIds.length :
        this.LENGTH)
      );

      const params: any = {
        vacancyCandidateIds,
        selectedVacancyIds: this.selectedVacancies.map(vacancy => vacancy._id)
      };

      requests.push(
        this.runProcess(params)
      );
      index++;
    } while (this.vacancyCandidateIds.length > 0);

    modal = this.modalService.open(BulkActionsLoadingModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'lg'
    });

    modal.componentInstance.title = `Enviando ${this.vacancyCandidateIds.length === 1 ? 'o candidato' : 'os candidatos'} para ${this.selectedVacancies.length === 1 ? 'outra oportunidade' : 'outras oportunidades'}.`
    modal.componentInstance.message = `Estamos aplicando a ação para ${this.vacancyCandidateIds.length === 1 ? 'o candidato selecionado' : 'os candidatos selecionados'}.<br>Aguarde, isso pode demorar alguns minutos!.`
    this.closeModal();

    of(...requests).pipe(
      mergeMap(request => request, numberOfSimultaneousRequests),
      forkJoin
    ).subscribe((response: any) => {
      this.loading = 'FINALIZED';
      this.toastrService.clear();
      this.toastrService.success(this.candidates.length > 1 ? `Os candidatos foram enviados para ${this.selectedVacancies.length === 1 ? 'a vaga' : 'as vagas'}.` : `O candidato foi indicado para ${this.selectedVacancies.length === 1 ? 'a vaga' : 'as vagas'}.`, 'Ação concluída!', {
        enableHtml: true
      });
      
      clearInterval(interval);
      modal.close();
    }, (err) => {
      clearInterval(interval);
      modal.close();
    });
  }
  
  private runProcess(params: any): Observable<any> {
    return Observable.create(observer => {
      this.vacancyCandidatesService.bulkSendVacancyCandidateForAnotherOpportunity(params)
        .subscribe(response => {
          observer.next();
          observer.complete();
        }, error => {
          observer.error(error);
        });
    });
  }

  public closeModal(): void {
    this.activeModal.close();
  }

  public onSelectedVacancyChange(event: any): void {
    this.selectedVacancies = [];

    if (!event) {
      return;
    }

    this.selectedVacancies = [event];
  }
}
